// src/components/pages/admin-dash/PaymentsListByFilter.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";

const CREDIT_PLAN_OPTIONS = [
  { value: "",         label: "All plans"  },
  { value: "SINGLE",   label: "Single"     },
  { value: "BASIC",    label: "Basic"      },
  { value: "GOLD",     label: "Gold"       },
  { value: "PLATINUM", label: "Platinum"   },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: "",       label: "All methods" },
  { value: "STRIPE", label: "Stripe"      },
  { value: "PAYPAL", label: "PayPal"      },
];

const STATUS_PAYMENT_OPTIONS = [
  { value: "",           label: "All statuses" },
  { value: "PROCESSING", label: "Processing"   },
  { value: "PAID_OUT",   label: "Paid out"     },
  { value: "ERROR",      label: "Error"        },
  { value: "REFUNDED",   label: "Refunded"     },
];

const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
    : String(n ?? "—");

const getDisplayKind = (p) => {
  if (p?.transactionType) return String(p.transactionType).replaceAll("_", " ");
  return p?.creditPlan ? "ONE TIME PURCHASE" : "SUBSCRIPTION";
};
const getDisplayPlan = (p) =>
  p?.subscription?.plan || p?.subscriptionPlan || p?.creditPlan || "—";
const getDisplaySubId = (p) =>
  p?.subscription?.id || p?.subscriptionId || "—";

export default function PaymentsListByFilter() {
  const [creditPlan, setCreditPlan]       = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [statusPayment, setStatusPayment] = useState("");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [didSearch, setDidSearch] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (creditPlan)    params.creditPlan    = creditPlan.toUpperCase();
      if (paymentMethod) params.paymentMethod = paymentMethod.toUpperCase();
      if (statusPayment) params.statusPayment = statusPayment.toUpperCase();
      const res = await axiosInstance.get("/admin/payment/list-by-filter", { params });
      setPayments(Array.isArray(res.data) ? res.data : []);
      setDidSearch(true);
    } catch {
      setError("Failed to load payments.");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCreditPlan(""); setPaymentMethod(""); setStatusPayment("");
    setPayments([]); setDidSearch(false); setError("");
  };

  const exportCsv = () => {
    if (payments.length === 0) return;
    const headers = [
      "id", "userId", "userName", "kind", "plan", "subscriptionId",
      "paymentMethod", "status", "amount", "idTransaction", "date",
    ];
    const lines = [headers.join(",")];
    payments.forEach((p) => {
      const row = [
        p.id, p.user?.id, p.user?.name,
        getDisplayKind(p), getDisplayPlan(p), getDisplaySubId(p),
        p.paymentMethod, p.status, p.amount, p.idTransaction,
        p.date?.split("T")[0] || "",
      ].map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`);
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `payments-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { key: "id",       label: "ID",       sort: true, width: "70px",
      render: (p) => <span className="tabular-nums" style={{ color: "var(--txt-2)" }}>#{p.id}</span> },
    { key: "user",     label: "User",     sort: true, value: (p) => p.user?.name || "",
      render: (p) => (
        <div>
          <div style={{ color: "var(--txt)" }} className="font-medium">{p.user?.name || "—"}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>#{p.user?.id ?? "?"}</div>
        </div>
      ),
    },
    { key: "kind",     label: "Kind",     sort: true, value: getDisplayKind,
      render: (p) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{getDisplayKind(p)}</span> },
    { key: "plan",     label: "Plan",     sort: true, value: getDisplayPlan,
      render: (p) => getDisplayPlan(p) },
    { key: "method",   label: "Method",   sort: true, value: (p) => p.paymentMethod || "",
      render: (p) => p.paymentMethod ? <Badge variant="neutral">{p.paymentMethod}</Badge> : "—" },
    { key: "status",   label: "Status",   sort: true, value: (p) => p.status || "",
      render: (p) => <StatusBadge status={p.status} /> },
    { key: "amount",   label: "Amount",   sort: true, align: "right", value: (p) => p.amount ?? 0,
      render: (p) => <span className="tabular-nums font-medium">{fmtUsd(p.amount)}</span> },
    { key: "txn",      label: "Txn",      sort: false,
      render: (p) => <span className="text-[11px] tabular-nums" style={{ color: "var(--txt-3)" }}>{p.idTransaction || "—"}</span> },
    { key: "date",     label: "Date",     sort: true, value: (p) => p.date || "",
      render: (p) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{p.date?.split("T")[0] || "—"}</span> },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Payments
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Search payments by plan, method, or status.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
          <SelectField label="Plan"   value={creditPlan}    onChange={setCreditPlan}    options={CREDIT_PLAN_OPTIONS} />
          <SelectField label="Method" value={paymentMethod} onChange={setPaymentMethod} options={PAYMENT_METHOD_OPTIONS} />
          <SelectField label="Status" value={statusPayment} onChange={setStatusPayment} options={STATUS_PAYMENT_OPTIONS} />
          <div className="flex items-end gap-2">
            <Button icon="search" onClick={handleSearch} loading={loading} size="md">Search
            </Button>
            {(creditPlan || paymentMethod || statusPayment || didSearch) && (
              <button
                type="button"
                onClick={handleReset}
                className="text-[12px] hover:underline px-1 pb-3"
                style={{ color: "var(--txt-2)" }}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{
            background: "var(--red-dim)",
            border: "1px solid var(--red-border)",
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      <AdminTable
        title={didSearch ? `${payments.length} results` : undefined}
        rightHeaderSlot={
          payments.length > 0 ? (
            <Button icon="download" size="sm" variant="ghost" onClick={exportCsv}>Export CSV
            </Button>
          ) : null
        }
        columns={columns}
        rows={payments}
        loading={loading}
        emptyIcon="credit-card"
        emptyMessage={
          didSearch
            ? "No payments match these filters."
            : "Pick filters above and search to begin."
        }
        defaultSort={{ key: "date", dir: "desc" }}
      />
    </div>
  );
}

/* ============================================================
   Tiny helpers (shared by Payments views)
   ============================================================ */
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label
        className="block text-[12px] font-medium mb-1.5 tracking-wide"
        style={{ color: "var(--txt)" }}
      >
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-full border appearance-none cursor-pointer pr-9"
          style={{
            background: "var(--surf-2)",
            color: "var(--txt)",
            borderColor: "var(--border-2)",
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px]"
          style={{ color: "var(--txt-3)" }}
        >
          ▾
        </span>
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  switch (status) {
    case "PAID_OUT":   return <Badge variant="teal">Paid out</Badge>;
    case "PROCESSING": return <Badge variant="gold">Processing</Badge>;
    case "ERROR":      return <Badge variant="red">Error</Badge>;
    case "REFUNDED":   return <Badge variant="blue">Refunded</Badge>;
    default:           return <Badge variant="neutral">{status || "—"}</Badge>;
  }
}
